import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Skeleton,
  Tooltip,
  IconButton,
  Collapse,
  Paper,
  TextField,
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  ClearAll as ClearAllIcon,
  CalendarMonth as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassIcon,
  Sync as SyncIcon,
  Gavel as GavelIcon,
  AccountBalance as AccountBalanceIcon,
  AttachMoney as MoneyIcon,
  Home as HomeIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import StripeAccountStatusCard from '../../components/financial/StripeAccountStatusCard';
import ownerPayoutService from '../../services/ownerPayoutService';

// ─────────────────────────────────────────────
// Config statuts
// ─────────────────────────────────────────────
const STATUS_CONFIG = {
  pending:               { color: 'warning',   muiColor: '#f57c00', bg: '#fff8e1', icon: <HourglassIcon fontSize="small" /> },
  processing:            { color: 'secondary', muiColor: '#7b1fa2', bg: '#f3e5f5', icon: <SyncIcon fontSize="small" /> },
  completed:             { color: 'success',   muiColor: '#2e7d32', bg: '#e8f5e9', icon: <CheckCircleIcon fontSize="small" /> },
  failed:                { color: 'error',     muiColor: '#c62828', bg: '#ffebee', icon: <ErrorIcon fontSize="small" /> },
  cancelled:             { color: 'default',   muiColor: '#757575', bg: '#eeeeee', icon: <CancelIcon fontSize="small" /> },
  deducted_for_penalty:  { color: 'error',     muiColor: '#e65100', bg: '#fff3e0', icon: <GavelIcon fontSize="small" /> },
};

const ALL_STATUSES = ['pending', 'processing', 'completed', 'failed', 'cancelled', 'deducted_for_penalty'];

const SORT_OPTIONS = [
  { value: 'created_at|DESC', label: 'payouts.sort.newestFirst' },
  { value: 'created_at|ASC',  label: 'payouts.sort.oldestFirst' },
  { value: 'net_amount|DESC', label: 'payouts.sort.amountDesc' },
  { value: 'net_amount|ASC',  label: 'payouts.sort.amountAsc' },
];

const PAGE_SIZE = 12;

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const formatAmount = (amount, currency = 'EUR') => {
  if (amount == null) return '—';
  try {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(amount);
  } catch {
    return `${Number(amount).toFixed(2)} ${currency}`;
  }
};

const getNormalizedBreakdownDisplay = (breakdown) => {
  if (!breakdown) {
    return null;
  }

  const grossAmount = Number(breakdown.gross_amount || 0);
  const finalPayoutAmount = Number(breakdown.final_payout_amount || 0);
  const stripeFeeAmount = Number(breakdown.stripe_fee_amount || 0);

  if (!breakdown.is_currency_converted) {
    return {
      stripeFeeAmount,
      stripeFeeCurrency: breakdown.stripe_fee_currency,
      netBeforeConversionAmount: Number(breakdown.net_before_conversion_amount || 0),
      netBeforeConversionCurrency: breakdown.net_before_conversion_currency,
      exchangeRate: Number(breakdown.exchange_rate || 0),
      exchangeRateCurrency: breakdown.gross_currency
    };
  }

  if (
    breakdown.type === 'damage_compensation' &&
    breakdown.gross_currency &&
    breakdown.gross_currency !== 'EUR' &&
    breakdown.final_payout_currency === 'EUR' &&
    grossAmount > 0 &&
    finalPayoutAmount > 0
  ) {
    const grossEquivalentInEur = finalPayoutAmount + Math.max(0, stripeFeeAmount);
    const displayExchangeRate = grossEquivalentInEur > 0 ? grossAmount / grossEquivalentInEur : 0;

    return {
      stripeFeeAmount: Math.max(0, stripeFeeAmount) * displayExchangeRate,
      stripeFeeCurrency: breakdown.gross_currency,
      netBeforeConversionAmount: finalPayoutAmount * displayExchangeRate,
      netBeforeConversionCurrency: breakdown.gross_currency,
      exchangeRate: displayExchangeRate,
      exchangeRateCurrency: breakdown.gross_currency
    };
  }

  return {
    stripeFeeAmount,
    stripeFeeCurrency: breakdown.stripe_fee_currency,
    netBeforeConversionAmount: Number(breakdown.net_before_conversion_amount || 0),
    netBeforeConversionCurrency: breakdown.net_before_conversion_currency,
    exchangeRate: Number(breakdown.exchange_rate || 0),
    exchangeRateCurrency: breakdown.gross_currency
  };
};

const BreakdownRow = ({ label, value, negative = false, strong = false }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75 }}>
    <Typography variant="body2" sx={{ color: '#666' }}>{label}</Typography>
    <Typography
      variant="body2"
      sx={{
        fontWeight: strong ? 800 : 700,
        color: negative ? '#c62828' : '#1a1a2e',
        textAlign: 'right'
      }}
    >
      {value}
    </Typography>
  </Box>
);

// ─────────────────────────────────────────────
// Sanitise et traduit les raisons d'échec brutes
// (messages Stripe internes, codes techniques, liens, etc.)
// ─────────────────────────────────────────────
const sanitizeFailureReason = (reason, t) => {
  if (!reason) return null;
  const r = String(reason).toLowerCase();

  if (r === 'manual_cancellation') {
    return t('payouts.failureReasons.manual_cancellation', 'Ce virement a été annulé.');
  }
  if (
    r.includes('onboarding') ||
    r.includes('account')
  ) {
    return (
      <span>
        {t(
          'payouts.failureReasons.onboarding_incomplete',
          "Votre compte n'est pas encore configuré pour recevoir des paiements. Rendez-vous dans vos paramètres pour finaliser votre compte Stripe."
        )}{' '}
        <Link
          to="/financial-owner"
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontWeight: 700, color: '#1a73e8' }}
        >
          {t('payouts.failureReasons.checkFinancialPage', 'Cliquez ici pour accéder à la page financière.')}
        </Link>
      </span>
    );
  }
  // Messages techniques internes Stripe — reformulations neutres obligatoires
  if (
    r.includes('insufficient') ||
    r.includes('stripe.com/docs') ||
    r.includes('4000') ||
    r.includes('test card') ||
    r.includes('available balance') ||
    r.includes('creating charges')
  ) {
    return t(
      'payouts.failureReasons.processing_error',
      'Une erreur est survenue lors du traitement du virement. Notre équipe a été informée et prendra en charge votre dossier dans les meilleurs délais.'
    );
  }
  // Erreur inconnue : message générique
  return t(
    'payouts.failureReasons.generic_error',
    'Une erreur technique est survenue lors de ce virement. Notre équipe a été informée.'
  );
};

// ─────────────────────────────────────────────
// Composant PayoutCard
// ─────────────────────────────────────────────
const PayoutCard = ({ payout, onDownloadPdf, downloadingId, t }) => {
  const cfg = STATUS_CONFIG[payout.status] || STATUS_CONFIG.pending;
  const isDownloading = downloadingId === payout.id;
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [penaltiesOpen, setPenaltiesOpen] = useState(false);
  const breakdown = payout.payout_breakdown;
  const listingTitle = breakdown?.listing_title || payout.reservation?.listing?.title;
  const normalizedDisplay = getNormalizedBreakdownDisplay(breakdown);

  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: 3,
        border: `1px solid ${cfg.muiColor}30`,
        transition: 'box-shadow 0.2s, transform 0.15s',
        '&:hover': { boxShadow: 6, transform: 'translateY(-2px)' },
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/* Header coloré */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${cfg.muiColor}18 0%, ${cfg.bg} 100%)`,
          borderBottom: `2px solid ${cfg.muiColor}40`,
          px: 2.5,
          pt: 2,
          pb: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Box sx={{ maxWidth: '60%' }}>
          <Typography
            variant="caption"
            sx={{ color: '#666', fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}
          >
            {t('payouts.virement', 'Virement')}
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: '#1a1a2e', fontSize: '0.82rem', lineHeight: 1.3 }}
            noWrap
            title={listingTitle || t('payouts.noListing', 'Logement non disponible')}
          >
            {listingTitle || t('payouts.noListing', '—')}
          </Typography>
        </Box>
        <Chip
          icon={cfg.icon}
          label={t(`payouts.status.${payout.status}`, payout.status)}
          color={cfg.color}
          size="small"
          sx={{ fontWeight: 600, borderRadius: 2, flexShrink: 0 }}
        />
      </Box>

      <CardContent sx={{ flex: 1, pt: 2, pb: 1 }}>
        {/* Montant net — mis en avant */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: cfg.muiColor }}>
            {formatAmount(payout.net_amount, payout.currency)}
          </Typography>
          <Typography variant="caption" sx={{ color: '#888' }}>
            {t('payouts.netAmount', 'Montant net reçu')}
          </Typography>
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        {breakdown && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
            <Chip
              size="small"
              label={breakdown.type === 'damage_compensation'
                ? t('payouts.type.damage_compensation', 'Compensation dommages')
                : t('payouts.type.classic', 'Payout classique')}
              sx={{ fontWeight: 700, bgcolor: breakdown.type === 'damage_compensation' ? '#fff3e0' : '#e8f5e9' }}
            />
            {breakdown.has_penalty && (
              <Chip
                size="small"
                color="error"
                label={`${t('payouts.penalty', 'Pénalité')} : ${formatAmount(breakdown.penalty_amount, breakdown.penalty_currency)}`}
                sx={{ fontWeight: 700 }}
              />
            )}
          </Box>
        )}

        {/* Date prévue */}
        {payout.scheduled_date && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CalendarIcon fontSize="small" sx={{ color: '#888', flexShrink: 0 }} />
            <Box>
              <Typography variant="caption" sx={{ color: '#888', display: 'block', lineHeight: 1.1 }}>
                {t('payouts.scheduledDate', 'Date prévue')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                {formatDate(payout.scheduled_date)}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Date d'exécution (si complété) */}
        {payout.processed_at && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CheckCircleIcon fontSize="small" sx={{ color: '#2e7d32', flexShrink: 0 }} />
            <Box>
              <Typography variant="caption" sx={{ color: '#888', display: 'block', lineHeight: 1.1 }}>
                {t('payouts.processedAt', 'Exécuté le')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#2e7d32' }}>
                {formatDate(payout.processed_at)}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Type de virement */}
        {payout.payout_type && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MoneyIcon fontSize="small" sx={{ color: '#888', flexShrink: 0 }} />
            <Box>
              <Typography variant="caption" sx={{ color: '#888', display: 'block', lineHeight: 1.1 }}>
                {t('payouts.payoutType', 'Type')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                {t(`payouts.type.${payout.payout_type}`, payout.payout_type)}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Motif d'échec */}
        {payout.failure_reason && payout.status !== 'completed' && (
          <Box sx={{ mt: 1.5 }}>
            <Alert severity="error" sx={{ borderRadius: 2, py: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.25 }}>
                {t('payouts.failureReason', 'Motif d\'échec')}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>
                {sanitizeFailureReason(payout.failure_reason, t)}
              </Typography>
            </Alert>
          </Box>
        )}

        {breakdown && (
          <Box sx={{ mt: 2 }}>
            <Button
              size="small"
              onClick={() => setDetailsOpen((value) => !value)}
              endIcon={
                <ExpandMoreIcon
                  sx={{
                    transform: detailsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }}
                />
              }
              sx={{ px: 0, fontWeight: 700 }}
            >
              {detailsOpen
                ? t('payouts.hideDetails', 'Masquer le détail')
                : t('payouts.showDetails', 'Voir le détail')}
            </Button>

            <Collapse in={detailsOpen} timeout="auto" unmountOnExit>
              <Paper elevation={0} sx={{ mt: 1.5, p: 1.5, borderRadius: 2, background: '#fafafa', border: '1px solid #eee' }}>
                <BreakdownRow
                  label={t('payouts.grossAmount', 'Montant brut')}
                  value={formatAmount(breakdown.gross_amount, breakdown.gross_currency)}
                />

                {breakdown.type === 'damage_compensation' ? (
                  <BreakdownRow
                    label={t('payouts.stripeFees', 'Frais Stripe')}
                    value={`- ${formatAmount(normalizedDisplay?.stripeFeeAmount, normalizedDisplay?.stripeFeeCurrency || breakdown.stripe_fee_currency)}`}
                    negative
                  />
                ) : (
                  <>
                    <BreakdownRow
                      label={t('payouts.commission', 'Commission Krillou')}
                      value={`- ${formatAmount(breakdown.commission_amount, breakdown.commission_currency)}`}
                      negative
                    />
                    <BreakdownRow
                      label={t('payouts.tax', 'TVA')}
                      value={`- ${formatAmount(breakdown.tax_amount, breakdown.tax_currency)}`}
                      negative
                    />
                    {breakdown.dispute_refund_amount > 0 && (
                      <BreakdownRow
                        label={t('payouts.disputeRefund', 'Remboursement litige')}
                        value={`- ${formatAmount(breakdown.dispute_refund_amount, breakdown.dispute_refund_currency || breakdown.gross_currency)}`}
                        negative
                      />
                    )}
                  </>
                )}

                <BreakdownRow
                  label={t('payouts.netBeforeConversion', 'Net avant conversion')}
                  value={formatAmount(
                    normalizedDisplay?.netBeforeConversionAmount,
                    normalizedDisplay?.netBeforeConversionCurrency || breakdown.net_before_conversion_currency
                  )}
                />

                {breakdown.has_penalty && (
                  <BreakdownRow
                    label={t('payouts.penaltyDeduction', 'Pénalité déduite')}
                    value={`- ${formatAmount(breakdown.penalty_amount, breakdown.penalty_currency)}`}
                    negative
                  />
                )}

                {normalizedDisplay?.exchangeRate > 0 && normalizedDisplay?.exchangeRateCurrency !== 'EUR' && (
                  <BreakdownRow
                    label={t('payouts.exchangeRate', 'Taux de change')}
                    value={`1 EUR = ${Number(normalizedDisplay.exchangeRate).toFixed(4)} ${normalizedDisplay.exchangeRateCurrency}`}
                  />
                )}

                <Divider sx={{ my: 1 }} />
                <BreakdownRow
                  label={t('payouts.finalPayout', 'Montant versé')}
                  value={formatAmount(breakdown.final_payout_amount, breakdown.final_payout_currency)}
                  strong
                />

                {breakdown.damage_description && (
                  <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {t('payouts.damageDescription', 'Description des dommages')}
                    </Typography>
                    <Typography variant="body2">{breakdown.damage_description}</Typography>
                  </Alert>
                )}
              </Paper>
            </Collapse>

            {/* Pénalités — accordéon (soldes à venir et transférés) */}
            {breakdown.has_penalty &&
              Array.isArray(breakdown.penalty_deductions) &&
              breakdown.penalty_deductions.length > 0 &&
              !['failed', 'cancelled'].includes(payout.status) && (
              <Box sx={{ mt: 1.5 }}>
                <Button
                  size="small"
                  color="error"
                  onClick={() => setPenaltiesOpen(v => !v)}
                  endIcon={
                    <ExpandMoreIcon
                      sx={{
                        transform: penaltiesOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                      }}
                    />
                  }
                  startIcon={<GavelIcon />}
                  sx={{ px: 0, fontWeight: 700 }}
                >
                  {penaltiesOpen
                    ? t('payouts.hidePenalties', 'Masquer les pénalités')
                    : t('payouts.showPenalties', `Voir les pénalités (${breakdown.penalty_count})`)}
                </Button>
                <Collapse in={penaltiesOpen} timeout="auto" unmountOnExit>
                  <Paper elevation={0} sx={{ mt: 1.5, p: 1.5, borderRadius: 2, background: '#fff8e1', border: '1px solid #f57c00' }}>
                    {breakdown.penalty_deductions.map((penalty, pIdx) => (
                      <Box
                        key={penalty.penalty_id || pIdx}
                        sx={{
                          mb: pIdx < breakdown.penalty_deductions.length - 1 ? 1.5 : 0,
                          pb: pIdx < breakdown.penalty_deductions.length - 1 ? 1.5 : 0,
                          borderBottom: pIdx < breakdown.penalty_deductions.length - 1 ? '1px solid #ffe0b2' : 'none'
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#c62828' }}>
                            − {formatAmount(penalty.deducted_amount, payout.currency)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#888' }}>
                            {penalty.date ? new Date(penalty.date).toLocaleDateString('fr-FR') : '—'}
                          </Typography>
                        </Box>
                        {penalty.reason && (
                          <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                            {penalty.reason}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Typography variant="caption" sx={{ color: '#888' }}>
                            {t('payouts.penalty.before', 'Avant')} : {formatAmount(penalty.previous_net_amount, payout.currency)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                            → {t('payouts.penalty.after', 'Après')} : {formatAmount(penalty.new_net_amount, payout.currency)}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Paper>
                </Collapse>
              </Box>
            )}
          </Box>
        )}
      </CardContent>

      <CardActions sx={{ px: 2, pb: 2, pt: 0, flexDirection: 'column', gap: 1 }}>
        {breakdown?.reservation_id && (
          <Button
            component={Link}
            to={`/reservations/${breakdown.reservation_id}`}
            variant="outlined"
            size="small"
            color="primary"
            fullWidth
            startIcon={<OpenInNewIcon />}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            {t('payouts.viewReservation', 'Voir la réservation')}
          </Button>
        )}
        <Tooltip title={t('payouts.downloadPdf', 'Télécharger le reçu PDF')}>
          <span style={{ width: '100%' }}>
            <Button
              variant="outlined"
              size="small"
              color="secondary"
              fullWidth
              startIcon={isDownloading ? <CircularProgress size={14} /> : <DownloadIcon />}
              onClick={() => onDownloadPdf(payout)}
              disabled={isDownloading || payout.status !== 'completed'}
              sx={{ borderRadius: 2, fontWeight: 600 }}
            >
              {isDownloading
                ? t('payouts.downloading', 'Génération…')
                : t('payouts.downloadPdfBtn', 'Reçu PDF')}
            </Button>
          </span>
        </Tooltip>
      </CardActions>
    </Card>
  );
};

// ─────────────────────────────────────────────
// Composant SkeletonCard
// ─────────────────────────────────────────────
const SkeletonCard = () => (
  <Card elevation={1} sx={{ borderRadius: 3, height: '100%' }}>
    <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
      <Skeleton variant="text" width="60%" height={20} />
      <Skeleton variant="text" width="80%" height={22} />
    </Box>
    <CardContent sx={{ pt: 2 }}>
      <Skeleton variant="text" width="50%" height={36} sx={{ mx: 'auto', mb: 1 }} />
      <Skeleton variant="rectangular" height={1} sx={{ mb: 1.5 }} />
      <Skeleton variant="text" width="70%" />
      <Skeleton variant="text" width="60%" />
    </CardContent>
    <CardActions sx={{ px: 2, pb: 2 }}>
      <Skeleton variant="rectangular" height={34} sx={{ flex: 1, borderRadius: 2 }} />
    </CardActions>
  </Card>
);

// ─────────────────────────────────────────────
// Composant StatsBar
// ─────────────────────────────────────────────
const StatsBar = ({ stats, t }) => {
  if (!stats) return null;

  const totalCount     = stats.total_count ?? 0;
  const completedAmt   = stats.total_completed ?? 0;
  const pendingAmt     = stats.total_pending ?? 0;
  const currency       = stats.currency ?? 'EUR';

  return (
    <Paper elevation={0} sx={{ borderRadius: 3, p: 2.5, mb: 3, border: '1px solid #e0e0e0', background: '#fafafa' }}>
      <Grid container spacing={2} justifyContent="center">
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a1a2e' }}>{totalCount}</Typography>
            <Typography variant="caption" sx={{ color: '#666' }}>{t('payouts.stats.total', 'Total virements')}</Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#2e7d32' }}>
              {formatAmount(completedAmt, currency)}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666' }}>{t('payouts.stats.completed', 'Montant viré')}</Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#f57c00' }}>
              {formatAmount(pendingAmt, currency)}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666' }}>{t('payouts.stats.pending', 'En attente')}</Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#1976d2' }}>
              {formatAmount(completedAmt + pendingAmt, currency)}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666' }}>{t('payouts.stats.totalAmount', 'Total général')}</Typography>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

// ─────────────────────────────────────────────
// Page principale
// ─────────────────────────────────────────────
const OwnerPayoutsPage = () => {
  const { t } = useTranslation('financial');
  const { user } = useAuth();
  const navigate = useNavigate();

  const [payouts, setPayouts]           = useState([]);
  const [stats, setStats]               = useState(null);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [page, setPage]                 = useState(1);
  const [totalPages, setTotalPages]     = useState(1);
  const [downloadingId, setDownloadingId] = useState(null);

  // Filtres
  const [showFilters, setShowFilters]   = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortValue, setSortValue]       = useState('created_at|DESC');

  // ── Chargement ──
  const fetchPayouts = useCallback(async (currentPage = 1) => {
    setLoading(true);
    setError(null);
    try {
      const [sort_by, sort_order] = sortValue.split('|');
      const params = { page: currentPage, limit: PAGE_SIZE, sort_by, sort_order };
      if (statusFilter !== 'all') params.status = statusFilter;

      const data = await ownerPayoutService.getOwnerPayouts(params);
      setPayouts(data.payouts || []);
      setStats(data.stats || null);
      setTotalPages(data.pagination?.totalPages || 1);
    } catch (err) {
      console.error('Erreur chargement virements:', err);
      setError(t('payouts.error', 'Impossible de charger vos virements. Veuillez réessayer.'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sortValue, t]);

  useEffect(() => {
    setPage(1);
    fetchPayouts(1);
  }, [statusFilter, sortValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Téléchargement PDF ──
  const handleDownloadPdf = async (payout) => {
    if (downloadingId) return;
    setDownloadingId(payout.id);
    try {
      const blob = await ownerPayoutService.downloadPayoutPdf(payout.id);
      if (!blob || blob.size === 0) throw new Error('PDF vide reçu');
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const date = formatDate(payout.processed_at || payout.scheduled_date).replace(/\//g, '-');
      link.setAttribute('download', `virement-krillou-${date}-${payout.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erreur téléchargement PDF virement:', err);
      alert(t('payouts.pdfError', 'Erreur lors du téléchargement du reçu PDF. Veuillez réessayer.'));
    } finally {
      setDownloadingId(null);
    }
  };

  // ── Reset filtres ──
  const handleClearFilters = () => {
    setStatusFilter('all');
    setSortValue('created_at|DESC');
    setPage(1);
  };

  const hasActiveFilters = statusFilter !== 'all' || sortValue !== 'created_at|DESC';

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* ── En-tête gradient ── */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
          p: 3,
          borderRadius: 2,
          background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
          color: 'white',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {t('payouts.title', 'Mes Virements')}
          </Typography>
          <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
            {t('payouts.subtitle', 'Tous vos virements Stripe, tous statuts confondus')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AccountBalanceIcon />}
          onClick={() => navigate('/financial-owner')}
          sx={{
            bgcolor: 'rgba(255,255,255,0.2)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.3)',
            color: 'white',
            fontWeight: 'medium',
            px: 3,
            py: 1.5,
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.3)',
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          {t('payouts.financialCenter', 'Centre Financier')}
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <StripeAccountStatusCard user={user} variant="banner" />
      </Box>

      {/* ── Stats ── */}
      {!loading && stats && <StatsBar stats={stats} t={t} />}
      {loading && (
        <Paper elevation={0} sx={{ borderRadius: 3, p: 2.5, mb: 3, border: '1px solid #e0e0e0' }}>
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map(i => (
              <Grid item xs={6} sm={3} key={i}>
                <Skeleton variant="text" width="50%" height={36} sx={{ mx: 'auto' }} />
                <Skeleton variant="text" width="70%" height={18} sx={{ mx: 'auto' }} />
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* ── Barre filtres ── */}
      <Paper elevation={0} sx={{ borderRadius: 3, p: 2, mb: 3, border: '1px solid #e0e0e0' }}>
        {/* Status chips */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#444', mr: 0.5, flexShrink: 0 }}>
            {t('payouts.filterStatus', 'Statut :')}
          </Typography>
          <Chip
            label={t('payouts.status.all', 'Tous')}
            onClick={() => { setStatusFilter('all'); setPage(1); }}
            color={statusFilter === 'all' ? 'primary' : 'default'}
            variant={statusFilter === 'all' ? 'filled' : 'outlined'}
            size="small"
            sx={{ fontWeight: 600 }}
          />
          {ALL_STATUSES.map(status => {
            const cfg = STATUS_CONFIG[status];
            return (
              <Chip
                key={status}
                label={t(`payouts.status.${status}`, status)}
                icon={cfg.icon}
                onClick={() => { setStatusFilter(status); setPage(1); }}
                color={statusFilter === status ? cfg.color : 'default'}
                variant={statusFilter === status ? 'filled' : 'outlined'}
                size="small"
                sx={{ fontWeight: 600 }}
              />
            );
          })}

          <Box sx={{ ml: 'auto', display: 'flex', gap: 1, flexShrink: 0 }}>
            <Tooltip title={t('payouts.toggleSort', 'Tri')}>
              <IconButton
                size="small"
                onClick={() => setShowFilters(v => !v)}
                color={showFilters ? 'primary' : 'default'}
                sx={{ border: '1px solid #ddd', borderRadius: 2 }}
              >
                <FilterListIcon />
              </IconButton>
            </Tooltip>
            {hasActiveFilters && (
              <Tooltip title={t('payouts.clearFilters', 'Réinitialiser')}>
                <IconButton
                  size="small"
                  onClick={handleClearFilters}
                  color="error"
                  sx={{ border: '1px solid #ffcdd2', borderRadius: 2 }}
                >
                  <ClearAllIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Tri */}
        <Collapse in={showFilters}>
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #f0f0f0' }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>{t('payouts.sortBy', 'Trier par')}</InputLabel>
              <Select
                value={sortValue}
                label={t('payouts.sortBy', 'Trier par')}
                onChange={(e) => { setSortValue(e.target.value); setPage(1); }}
              >
                {SORT_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {t(opt.label, opt.label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Collapse>
      </Paper>

      {/* ── Erreur ── */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── Grille de cartes ── */}
      {loading ? (
        <Grid container spacing={3}>
          {Array.from({ length: PAGE_SIZE }).map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <SkeletonCard />
            </Grid>
          ))}
        </Grid>
      ) : payouts.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            textAlign: 'center',
            py: 8,
            borderRadius: 3,
            border: '2px dashed #e0e0e0',
            background: '#fafafa',
          }}
        >
          <WalletIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#aaa', mb: 1 }}>
            {t('payouts.empty.title', 'Aucun virement')}
          </Typography>
          <Typography variant="body2" sx={{ color: '#bbb' }}>
            {statusFilter !== 'all'
              ? t('payouts.empty.filteredMessage', 'Aucun virement avec ce statut.')
              : t('payouts.empty.message', 'Vous n\'avez pas encore reçu de virement.')}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {payouts.map(payout => (
            <Grid item xs={12} sm={6} md={4} key={payout.id}>
              <PayoutCard
                payout={payout}
                onDownloadPdf={handleDownloadPdf}
                downloadingId={downloadingId}
                t={t}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Pagination ── */}
      {!loading && totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => { setPage(value); fetchPayouts(value); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Container>
  );
};

export default OwnerPayoutsPage;
